/**
 * Module 'cloud-init': a cloud VM that joins Orgus by itself (ADR 0105 decision 1).
 *
 * Every hyperscaler boots a new Linux VM through cloud-init and takes a `#cloud-config` document
 * in its "user data" field. Add machine mints a join ticket (ADR 0090) and writes this document
 * around it: the VM creates an unprivileged `orgus` user, runs the one-line installer (ADR 0100)
 * as that user with the ticket, and the machine is approved, installed and online with no one
 * touching it. The document holds the ticket, so it is shown once, like the join link, and is
 * never served from a public page.
 *
 * Only characters no shell or YAML reader interprets reach the document: the join link, the
 * machine name and the hub address are validated, anything else throws.
 */
import { JOIN_PAGE, JOIN_TICKET_RE } from './pairing-tickets.ts';
import { INSTALL_ORIGIN, INSTALL_SCRIPT_PATHS } from './fleet.ts';

/** The user the agent runs as on the VM: no sudo, no password, its own home. */
export const CLOUD_INIT_USER = 'orgus';
/** Where the ticket waits for the installer, deleted right after. */
export const CLOUD_INIT_TICKET_FILE = `/home/${CLOUD_INIT_USER}/.orgus-join`;

/** A host-name safe machine name (the same rule as node pools): letters, digits, dashes, 63 at most. */
export const CLOUD_MACHINE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,62}$/;
const HOST_RE = /^[A-Za-z0-9.-]+(:[0-9]{1,5})?$/;
const HUB_URL_RE = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?$/;
/** A public git repository over https: host, path segments of safe characters, no credentials, no query. */
const REPO_URL_RE = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?(\/[A-Za-z0-9._~-]+)+$/;

/** Where a cloned repository goes on the VM: one folder per repository, in the agent user's home. */
export const CLOUD_INIT_WORK_DIR = `/home/${CLOUD_INIT_USER}/work`;

/** The folder a repository address is cloned into: its last path segment without .git. */
export function cloudRepoFolder(repoUrl: string): string {
  if (!REPO_URL_RE.test(repoUrl)) throw new Error('the repository must be a public https address, for example https://github.com/org/repo');
  const last = repoUrl.split('/').pop()!.replace(/\.git$/, '');
  if (!last || /^\.+$/.test(last)) throw new Error('the repository address names no repository');
  return `${CLOUD_INIT_WORK_DIR}/${last}`;
}

export interface CloudInitInput {
  /** The join link Add machine minted: `https://<console>/join/<ticket>`. */
  joinUrl: string;
  /** The name the machine enrols under; the VM's host name when absent. */
  name?: string;
  /** A local hub to pair with instead of the built-in server (AgentDownloads.pairWith.url). */
  hubUrl?: string;
  /**
   * A public git repository to clone for the agent's user before the agent joins, so the new
   * machine has a project to work in (its folder: cloudRepoFolder). A private repository needs a
   * credential on the machine and is not cloned here.
   */
  repoUrl?: string;
}

/** Where each provider's console takes the document. The field names are theirs, as their forms show them. */
export const CLOUD_USER_DATA_FIELDS: readonly { provider: string; field: string }[] = [
  { provider: 'AWS EC2', field: 'Advanced details, User data' },
  { provider: 'Azure', field: 'Advanced, Custom data' },
  { provider: 'Google Cloud', field: 'Management, Metadata, key user-data' },
  { provider: 'Hetzner Cloud', field: 'Cloud config' },
  { provider: 'DigitalOcean', field: 'Advanced options, Add initialization scripts' },
  { provider: 'Oracle Cloud', field: 'Advanced options, Cloud-init script' },
];

/**
 * The join link's parts, or a sentence saying what is wrong with it. https only, except a
 * loopback console (a local hub, demo data, tests), which a VM could not reach anyway.
 */
export function parseJoinUrl(url: string): { host: string; ticket: string } {
  const m = /^(https?):\/\/([^/]+)\/join\/([^/?#]+)$/.exec(url);
  const loopback = m?.[2] !== undefined && /^(127\.0\.0\.1|localhost)(:[0-9]{1,5})?$/.test(m[2]);
  if (!m || (m[1] !== 'https' && !loopback) || !HOST_RE.test(m[2]!) || !JOIN_TICKET_RE.test(m[3]!)) throw new Error(`the join link must read https://<console>${JOIN_PAGE}/<ticket>`);
  return { host: m[2]!, ticket: m[3]! };
}

/**
 * The `#cloud-config` document for a Linux VM. In order on the VM: the `orgus` user (no sudo),
 * curl installed, the ticket written for that user only (deferred, so the user exists first),
 * lingering turned on so the agent's systemd user unit runs without a login, the one-line
 * installer run as `orgus` with the ticket and name, and the ticket file removed. The installer
 * checks the binary against the signed release manifest before it runs, as always. With a
 * repository, git is installed too and the repository is cloned as `orgus` before the agent
 * joins, so the agent finds it on its first look.
 */
export function cloudInitUserData(input: CloudInitInput): string {
  parseJoinUrl(input.joinUrl);
  if (input.name !== undefined && !CLOUD_MACHINE_NAME_RE.test(input.name)) throw new Error('the machine name takes letters, digits and dashes, 63 at most, starting with a letter or digit');
  if (input.hubUrl !== undefined && !HUB_URL_RE.test(input.hubUrl)) throw new Error('the hub address must read https://<host>[:port]');
  const repoDir = input.repoUrl !== undefined ? cloudRepoFolder(input.repoUrl) : null;
  const args = ['--ticket-file', CLOUD_INIT_TICKET_FILE];
  if (input.name) args.push('--name', input.name);
  if (input.hubUrl) args.push('--hub', input.hubUrl);
  const install = `curl -fsSL ${INSTALL_ORIGIN}${INSTALL_SCRIPT_PATHS.sh} | sh -s -- ${args.join(' ')}`;
  return [
    '#cloud-config',
    '# Orgus node agent: this VM joins your Orgus workspace by itself (made by Add machine).',
    '# The join link below works once, for 15 minutes; make a new document for each VM.',
    'users:',
    '  - default',
    `  - name: ${CLOUD_INIT_USER}`,
    '    gecos: Orgus node agent',
    '    shell: /bin/bash',
    '    lock_passwd: true',
    'packages:',
    '  - curl',
    ...(repoDir ? ['  - git'] : []),
    'write_files:',
    `  - path: ${CLOUD_INIT_TICKET_FILE}`,
    `    owner: ${CLOUD_INIT_USER}:${CLOUD_INIT_USER}`,
    "    permissions: '0600'",
    '    defer: true',
    `    content: "${input.joinUrl}\\n"`,
    'runcmd:',
    `  - [sh, -c, "loginctl enable-linger ${CLOUD_INIT_USER} || true"]`,
    ...(repoDir ? [`  - [su, -s, /bin/sh, -l, ${CLOUD_INIT_USER}, -c, "mkdir -p ${CLOUD_INIT_WORK_DIR} && git clone --quiet ${input.repoUrl} ${repoDir}"]`] : []),
    `  - [su, -s, /bin/sh, -l, ${CLOUD_INIT_USER}, -c, "${install}"]`,
    `  - [rm, -f, ${CLOUD_INIT_TICKET_FILE}]`,
    '',
  ].join('\n');
}

/** What the Add machine panel says next to the document. */
export const CLOUD_INIT_SENTENCE = 'Paste this as the user data of a new Linux VM. It installs the agent as an unprivileged user and the machine joins by itself, usually within two minutes of boot.';
